import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import ProductCard from '@/components/ProductCard';
import { useQuickEdit } from '@/hooks/useQuickEdit';
import { useHomepageSectionMutation } from '@/hooks/useHomepageSectionMutation';
import QuickEditInline from '@/components/quickedit/QuickEditInline';

interface FeaturedProductsProps {
  section: any;
}

export default function FeaturedProducts({ section }: FeaturedProductsProps) {
  const products: any[] = section.products ?? [];
  const headingId = `featured-heading-${section.id}`;

  const { isActive } = useQuickEdit();
  const mutation = useHomepageSectionMutation(section.id);

  if (products.length === 0) return null;

  return (
    <section aria-labelledby={headingId} className="bg-cream">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Header with decorative separator */}
        <div className="text-center mb-12">
          <h2
            id={headingId}
            className="font-display text-3xl font-bold text-sage-900 sm:text-4xl"
          >
            {isActive ? (
              <QuickEditInline
                value={section.title || 'Featured Products'}
                onSave={(val) => mutation.mutateAsync({ title: val, content: section.content })}
                label="Featured products title"
                as="span"
                className="font-display text-3xl font-bold text-sage-900 sm:text-4xl"
              />
            ) : (section.title || 'Featured Products')}
          </h2>
          {section.subtitle && (
            <p className="mt-2 text-sage-600 max-w-xl mx-auto">
              {isActive ? (
                <QuickEditInline
                  value={section.subtitle}
                  onSave={(val) => mutation.mutateAsync({ subtitle: val, content: section.content })}
                  label="Featured products subtitle"
                  as="span"
                  className="text-sage-600"
                />
              ) : section.subtitle}
            </p>
          )}
          <div className="mt-4 flex items-center justify-center gap-3" aria-hidden="true">
            <div className="h-px w-12 bg-sage-300" />
            <Sparkles className="h-4 w-4 text-sage-400" />
            <div className="h-px w-12 bg-sage-300" />
          </div>
        </div>

        {/* Product grid */}
        <ul className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4 list-none p-0">
          {products.map((product: any) => (
            <li key={product.id}>
              <ProductCard product={product} />
            </li>
          ))}
        </ul>

        <div className="mt-10 text-center">
          <Link
            to="/shop"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-sage-700 hover:text-sage-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded transition-colors"
          >
            View all products
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}
